import { useEffect, useRef, useState, useMemo } from 'react';
import { getWorkspaceId, useAppSelector } from '@zonofi/zono-money-store';

interface UseTokenizedPaginatedListProps<T> {
  fetchDataApiCall: (params: { nextToken?: string; pageSize: number }) => void;
  apiResponseData: T[];
  nextToken?: string;
  isLoading: boolean;
  isError?: boolean;
  uniqueKey: keyof T;
  dependencies?: any[];
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 20;

export const useTokenizedPaginatedList = <T>({
  fetchDataApiCall,
  apiResponseData = [],
  nextToken = undefined,
  isLoading = false,
  isError = false,
  uniqueKey,
  dependencies = [],
  pageSize = DEFAULT_PAGE_SIZE,
}: UseTokenizedPaginatedListProps<T>) => {
  const [data, setData] = useState<T[]>([]);
  const [currentToken, setCurrentToken] = useState<string>();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const fetchedTokensRef = useRef<Set<string>>(new Set());
  const scrollTimeout = useRef<NodeJS.Timeout | number | null>(null);
  const hasMoreDataRef = useRef(true);
  const paginationLock = useRef(false);
  const isInitialLoad = useRef(true);
  const isResettingRef = useRef(false);

  const onScroll = () => {
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      const node = lastItemRef.current;
      const justReset = isInitialLoad.current && data.length === 0;

      if (
        node &&
        !isLoading &&
        !isPageLoading &&
        !justReset &&
        !paginationLock.current &&
        hasMoreDataRef.current &&
        nextToken &&
        nextToken !== 'stop' &&
        !fetchedTokensRef.current.has(nextToken)
      ) {
        const rect = node.getBoundingClientRect();
        const reachedBottom = rect.bottom - 50 <= window.innerHeight;
        if (reachedBottom) {
          paginationLock.current = true;
          setIsPageLoading(true);
          setCurrentToken(nextToken);
        }
      }
    }, 200);
  };

  // Reset and fetch initial data when dependencies change
  useEffect(() => {
    try {
      resetAll();
      fetchDataApiCall({ pageSize });
    } catch (e) {
      console.error('Error fetching data:', e);
    }
  }, [sellerWorkspaceId, refreshNonce, ...dependencies]);

  // Fetch data when token changes (for subsequent pages)
  useEffect(() => {
    isResettingRef.current = false;
    if (!currentToken) return;
    if (
      currentToken &&
      currentToken !== 'stop' &&
      !fetchedTokensRef.current.has(currentToken) &&
      !isResettingRef.current &&
      hasMoreDataRef.current &&
      !isError &&
      !isLoading
    ) {
      fetchDataApiCall({ nextToken: currentToken, pageSize });
    }
  }, [currentToken]);

  // Check if more data should be loaded automatically (when content doesn't fill viewport)
  const checkAndLoadMoreData = () => {
    const node = lastItemRef.current;
    if (
      node &&
      !isLoading &&
      !isPageLoading &&
      !paginationLock.current &&
      hasMoreDataRef.current &&
      nextToken &&
      nextToken !== 'stop' &&
      !fetchedTokensRef.current.has(nextToken)
    ) {
      const rect = node.getBoundingClientRect();
      const isContentBelowViewport = rect.bottom > window.innerHeight;

      // If content doesn't extend below viewport, load more data
      if (!isContentBelowViewport) {
        paginationLock.current = true;
        setIsPageLoading(true);
        setCurrentToken(nextToken);
      }
    }
  };

  // Process API response data
  useEffect(() => {
    if (!isLoading && !isError && apiResponseData) {
      setData((prevData) => {
        const merged =
          currentToken === undefined
            ? apiResponseData
            : [...prevData, ...apiResponseData];
        const map = new Map<string, T>();
        for (const item of merged) {
          map.set(String(item[uniqueKey]), item);
        }
        return Array.from(map.values());
      });

      // Track fetched tokens
      if (currentToken) {
        fetchedTokensRef.current.add(currentToken);
      }

      // Determine if there's more data
      hasMoreDataRef.current =
        !!nextToken &&
        nextToken !== 'stop' &&
        apiResponseData.length >= pageSize;

      setIsPageLoading(false);
      paginationLock.current = false;
      isInitialLoad.current = false;

      // Check if we need to load more data to fill the viewport
      setTimeout(() => {
        checkAndLoadMoreData();
      }, 100);
    } else if (isError) {
      setIsPageLoading(false);
      paginationLock.current = false;
      isInitialLoad.current = false;
      hasMoreDataRef.current = false;
    }
  }, [apiResponseData, isLoading, isError, nextToken]);

  const resetAll = () => {
    isResettingRef.current = true;
    setCurrentToken(undefined);
    setIsPageLoading(false);
    fetchedTokensRef.current.clear();
    hasMoreDataRef.current = true;
    paginationLock.current = false;
    isInitialLoad.current = true;
  };

  const refresh = () => {
    setRefreshNonce((prev) => prev + 1);
  };

  const hasMoreData = hasMoreDataRef.current;

  return {
    data,
    currentToken,
    nextToken,
    lastItemRef,
    onScroll,
    resetAll,
    refresh,
    isPageLoading,
    hasMoreData,
    isEndOfList: !hasMoreData,
  };
};
